import { ParsedFinancialCommand } from "@/lib/assistant-parser";

export type AssistantModelBackend = "webgpu" | "wasm" | "none";

export interface AssistantModelManifest {
  modelId: string;
  version: string;
  quantization: string;
  hash: string;
}

interface AssistantWorkerRequest {
  id: string;
  input: string;
  backend: Exclude<AssistantModelBackend, "none">;
  categoryNames?: string[];
  accountNames?: string[];
}

interface AssistantWorkerResponse {
  id: string;
  parsed: ParsedFinancialCommand | null;
}

interface AssistantWorkerLike {
  onmessage: ((event: MessageEvent<AssistantWorkerResponse>) => void) | null;
  onerror: ((event: Event) => void) | null;
  postMessage: (payload: AssistantWorkerRequest) => void;
  terminate: () => void;
}

interface PendingRequest {
  resolve: (value: ParsedFinancialCommand | null) => void;
  timerId: ReturnType<typeof setTimeout>;
}

export interface LocalModelParseOptions {
  timeoutMs?: number;
  categoryNames?: string[];
  accountNames?: string[];
}

const ASSISTANT_MODEL_MANIFEST_KEY = "assistant:model-manifest";
const DEFAULT_WORKER_TIMEOUT_MS = 1200;

let assistantWorker: AssistantWorkerLike | null = null;
let assistantWorkerRequestSeq = 0;
let assistantWorkerFactoryOverride: (() => AssistantWorkerLike) | null = null;
const pendingRequests = new Map<string, PendingRequest>();

function isParsedFinancialCommand(payload: unknown): payload is ParsedFinancialCommand {
  if (!payload || typeof payload !== "object") return false;
  const candidate = payload as Partial<ParsedFinancialCommand>;
  const hasValidType = candidate.type === "expense" || candidate.type === "income";
  return Boolean(
    hasValidType
    && typeof candidate.amount === "number"
    && typeof candidate.description === "string"
    && typeof candidate.originalText === "string",
  );
}

function buildDefaultWorkerFactory(): (() => AssistantWorkerLike) | null {
  if (typeof Worker === "undefined") return null;
  return () => {
    const worker = new Worker(new URL("./assistant-local-model.worker.ts", import.meta.url), { type: "module" });
    return worker as AssistantWorkerLike;
  };
}

function resolveAssistantWorkerFactory(): (() => AssistantWorkerLike) | null {
  return assistantWorkerFactoryOverride ?? buildDefaultWorkerFactory();
}

function flushPendingRequests() {
  for (const pending of pendingRequests.values()) {
    clearTimeout(pending.timerId);
    pending.resolve(null);
  }
  pendingRequests.clear();
}

function ensureAssistantWorker(): AssistantWorkerLike | null {
  if (assistantWorker) return assistantWorker;

  const factory = resolveAssistantWorkerFactory();
  if (!factory) return null;

  assistantWorker = factory();
  assistantWorker.onmessage = (event) => {
    const payload = event.data;
    const pending = pendingRequests.get(payload.id);
    if (!pending) return;
    clearTimeout(pending.timerId);
    pendingRequests.delete(payload.id);
    pending.resolve(isParsedFinancialCommand(payload.parsed) ? payload.parsed : null);
  };
  assistantWorker.onerror = () => {
    flushPendingRequests();
  };
  return assistantWorker;
}

function getDefaultStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

export function negotiateLocalModelBackend(
  nav: Partial<Navigator> = (typeof navigator !== "undefined" ? navigator : {}),
  hasWebAssembly: boolean = typeof WebAssembly !== "undefined",
): AssistantModelBackend {
  if (!hasWebAssembly) return "none";
  if ("gpu" in nav) return "webgpu";
  return "wasm";
}

export function readCachedModelManifest(storage: Storage | null = getDefaultStorage()): AssistantModelManifest | null {
  if (!storage) return null;
  const rawManifest = storage.getItem(ASSISTANT_MODEL_MANIFEST_KEY);
  if (!rawManifest) return null;
  try {
    const parsed = JSON.parse(rawManifest) as AssistantModelManifest;
    if (!parsed.modelId || !parsed.version || !parsed.quantization || !parsed.hash) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedModelManifest(
  manifest: AssistantModelManifest,
  storage: Storage | null = getDefaultStorage(),
): void {
  if (!storage) return;
  storage.setItem(ASSISTANT_MODEL_MANIFEST_KEY, JSON.stringify(manifest));
}

export function clearCachedModelManifest(storage: Storage | null = getDefaultStorage()): void {
  if (!storage) return;
  storage.removeItem(ASSISTANT_MODEL_MANIFEST_KEY);
}

export function isModelCacheUpToDate(
  target: AssistantModelManifest,
  cached: AssistantModelManifest | null,
): boolean {
  if (!cached) return false;
  return (
    cached.modelId === target.modelId
    && cached.version === target.version
    && cached.quantization === target.quantization
    && cached.hash === target.hash
  );
}

export function setAssistantModelWorkerFactory(factory: (() => AssistantWorkerLike) | null): void {
  assistantWorkerFactoryOverride = factory;
}

export function resetAssistantModelWorkerRuntime(): void {
  if (assistantWorker) {
    assistantWorker.terminate();
    assistantWorker = null;
  }
  assistantWorkerRequestSeq = 0;
  flushPendingRequests();
}

export async function tryParseWithLocalModel(
  input: string,
  options: LocalModelParseOptions = {},
): Promise<ParsedFinancialCommand | null> {
  const normalizedInput = input.trim();
  if (!normalizedInput) return null;

  const backend = negotiateLocalModelBackend();
  if (backend === "none") return null;

  const worker = ensureAssistantWorker();
  if (!worker) return null;

  const requestId = `assistant-req-${++assistantWorkerRequestSeq}`;
  const timeoutMs = options.timeoutMs ?? DEFAULT_WORKER_TIMEOUT_MS;

  return new Promise<ParsedFinancialCommand | null>((resolve) => {
    const timerId = setTimeout(() => {
      pendingRequests.delete(requestId);
      resolve(null);
    }, timeoutMs);

    pendingRequests.set(requestId, { resolve, timerId });

    try {
      worker.postMessage({
        id: requestId,
        input: normalizedInput,
        backend,
        categoryNames: options.categoryNames,
        accountNames: options.accountNames,
      });
    } catch {
      clearTimeout(timerId);
      pendingRequests.delete(requestId);
      resolve(null);
    }
  });
}
